import { observer } from 'mobx-react';
import React, { ComponentProps, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { dataHooks } from '../../../dataHooks';
import { AppContextProvider } from '../../AppLoader';
import { ThemedText, ThemedTextButton } from '../../ThemedComponents';
import { EnforcementCard } from '../PremiumUsers2FaEnforcement/EnforcementCard';
import { SkipLink } from '../PremiumUsers2FaEnforcement/SkipEnforcementButton';

/** Reports the Dealer view event once per mounted step, as `PostLoginModal` does. */
export const useDealerOfferView = () => {
  const { rootStore } = useContext(AppContextProvider);
  useEffect(() => {
    const offerGuid = rootStore.postLoginStore.postLoginOffer?.offerGuid;
    if (offerGuid) {
      rootStore.dealerApi.sendDealerViewEvent(offerGuid);
    }
  }, []);
};

/** "Have more to update? Go to Account Settings". */
export const AccountSettingsFooter: React.FC = () => {
  const { rootStore } = useContext(AppContextProvider);
  return (
    <ThemedText size="small" weight="thin">
      <Trans i18nKey="post.login.footer">
        Have more to update? Go to
        <ThemedTextButton
          dataHook={dataHooks.postLogin.common.accountSettingsTextButton}
          as="a"
          onClick={() => {
            rootStore.postLoginStore.sendAccountSettingBiEvents();
            rootStore.navigationStore.redirect(
              'https://manage.wix.com/account/account-settings',
            );
          }}
          size="small"
          weight="thin"
          skin="standard"
        >
          Account Settings
        </ThemedTextButton>
      </Trans>
    </ThemedText>
  );
};

/** "Not now" link: leaves the offer, with a loader while redirecting. */
export const NotNowLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  const [isLeaving, setIsLeaving] = useState(false);
  return (
    <SkipLink
      label={t('post.login.not.now')}
      isLoading={isLeaving}
      onClick={() => {
        setIsLeaving(true);
        onClick();
      }}
    />
  );
};

/**
 * Card shell for the redesigned Dealer offer screens: the enforcement redesign's
 * `EnforcementCard` plus the Dealer view event and the Account Settings footer.
 */
export const DealerOfferCard: React.FC<ComponentProps<typeof EnforcementCard>> =
  observer((props) => {
    useDealerOfferView();
    return (
      <EnforcementCard
        priority="primary"
        footer={<AccountSettingsFooter />}
        {...props}
      />
    );
  });
